import classnames from 'classnames';
import { debounce } from 'lodash';
import { useReducer, useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';

import Slider from '@krea/common/shared-components/fields/slider';
import { usePostFieldRequest } from '@krea/common/store/field-requests/hooks/mutations';
import { useGetOneFieldRequest } from '@krea/common/store/field-requests/hooks/queries';
import { NormalizeRequestedPaymentTimeMonths } from '@krea/common/utils';

import FieldRequestPreviewHeader from '../FieldRequestPreviewHeader';
import FieldRequestSaved from '../FieldRequestSaved';

const PAYBACK_TIME_MONTHS_VALUES = [
  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 24, 30, 36, 42, 48, 54, 60,
];

const REAL_ESTATE_PAYBACK_TIME_MONTHS_VALUES = [
  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 24, 30, 36, 42, 48, 54, 60,
];

const LEASING_PAYBACK_TIME_MONTHS_VALUES = [
  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78,
  84,
];

const RepaymentTimeForm = ({
  fieldRequestID,
  isPreviewMode,
  isRealEstate,
  isLeasing,
  eligibleForLeasingBids,
  withInputLabel,
  t,
}) => {
  const { data: { submittedData, field, displayName } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error: err } =
    usePostFieldRequest(fieldRequestID);

  const error = err?.data?.errors?.[0]?.message;

  function reducer(state, action) {
    switch (action.type) {
      case 'value':
        return { ...state, value: action.payload };
      case 'isCustomValue':
        return { ...state, isCustomValue: !state.isCustomValue };
      case 'touched':
        return {
          ...state,
          touched: action.payload,
        };
      case 'changeValue':
        return {
          ...state,
          value: action.payload,
          touched: state.value !== action.payload,
        };
      default:
        throw new Error();
    }
  }

  const customValues = (submittedData) => {
    switch (true) {
      case isRealEstate:
        return {
          customValuesList: REAL_ESTATE_PAYBACK_TIME_MONTHS_VALUES,
          isCustomValue:
            REAL_ESTATE_PAYBACK_TIME_MONTHS_VALUES.indexOf(submittedData) ===
            -1,
        };
      case eligibleForLeasingBids:
        return {
          customValuesList: LEASING_PAYBACK_TIME_MONTHS_VALUES,
          isCustomValue:
            LEASING_PAYBACK_TIME_MONTHS_VALUES.indexOf(submittedData) === -1,
        };
      default:
        return {
          customValuesList: PAYBACK_TIME_MONTHS_VALUES,
          isCustomValue:
            PAYBACK_TIME_MONTHS_VALUES.indexOf(submittedData) === -1,
        };
    }
  };

  const [state, setState] = useReducer(reducer, {
    value: Number.isInteger(submittedData) ? submittedData : '',
    isCustomValue: customValues(submittedData).isCustomValue,
    touched: false,
  });

  const { value } = state;

  const numberClosestToValue = (numbers, value) => {
    const closest = numbers.reduce((prev, curr) => {
      return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
    });

    return closest;
  };

  const validateValue = (value) => {
    const currentValues = customValues(submittedData).customValuesList;
    const currentValuesLastItem = currentValues[currentValues.length - 1];
    const number = numberClosestToValue(currentValues, value);

    let validValue;

    switch (true) {
      case value < currentValues[0]:
        validValue = currentValues[0];
        break;

      case value > currentValuesLastItem:
        validValue = currentValuesLastItem;
        break;

      default:
        validValue = number;
        break;
    }

    return validValue;
  };

  const onBlur = (state) => {
    const { touched, value } = state;

    const currentValue = validateValue(value);
    setState({ type: 'changeValue', payload: currentValue });

    if (touched) {
      setState({ type: 'touched', payload: false });
      postFieldRequest({ value: currentValue });
    }
  };

  useEffect(() => {
    isLeasing &&
      (!submittedData || submittedData === 0) &&
      setState({ type: 'changeValue', payload: 60 });
    onBlur(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedFunc = debounce((state) => onBlur(state), 2500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const onCustomValueToggle = () => {
    if (state.isCustomValue) {
      const values = customValues(submittedData).customValuesList;
      const number = numberClosestToValue(values, state.value);

      setState({ type: 'changeValue', payload: number });
    }

    setState({ type: 'isCustomValue' });
  };

  // See this.onSliderChange for why this is needed
  const _onSliderChangeRest = (sliderValue) => {
    setState({ type: 'changeValue', payload: sliderValue });
  };
  // See this.onSliderChange for why this is needed

  const _debouncedOnSliderChangeRest = debounce(_onSliderChangeRest, 1000);

  const onSliderChange = ([value]) => {
    const currentValue = customValues(submittedData).customValuesList;

    const sliderValue = currentValue[value];

    // If this is the last value in the slider we have to also debounce the rest
    // of this function, because dragging off the end of the slider causes a LOT
    // of events
    let isLastValue = value === currentValue.length - 1;

    if (isLastValue) {
      // debounce the slider change events at this point if "dragging off the end"
      _debouncedOnSliderChangeRest(sliderValue);
    } else {
      // otherwise just carry on as usual
      _onSliderChangeRest(sliderValue);
    }
  };

  const onChange = ({ target }) => {
    let value = parseInt(target.value.replace(/\s+/g, ''), 10);

    if (isNaN(value)) {
      value = '';
    }

    setState({ type: 'changeValue', payload: value });
  };

  const onKeyDown = (e) => {
    const { value } = state;

    if (e.key === 'Enter') {
      e.preventDefault();
      postFieldRequest({ value: value === '' ? null : value });
    }
  };

  const formClasses = classnames('input-form', {
    'input-form-padded': isPreviewMode,
  });

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          label={displayName}
          value={
            <>
              {(submittedData || submittedData === 0) &&
                NormalizeRequestedPaymentTimeMonths(value)}
            </>
          }
        />
      ) : (
        <form
          className={formClasses}
          onKeyDown={onKeyDown}
          onBlur={() => onBlur(state)}
          onSubmit={(e) => e.preventDefault()}
        >
          <Slider
            id={`input_${field}`}
            label={withInputLabel ? displayName : null}
            className="flex-column flex-sm-row flex-wrap align-items-start align-items-sm-center"
            htmlFor={`input_${field}`}
            onSliderChange={onSliderChange}
            onValueChange={onChange}
            value={value}
            values={customValues(submittedData).customValuesList}
            formatter={NormalizeRequestedPaymentTimeMonths}
            placeholder={t('iaq.repaymentTime.valuePlaceholder')}
            postfix={t('commons.month', { count: state.value })}
            isCustomInput={state.isCustomValue}
            onCustomToggle={onCustomValueToggle}
            error={error}
          />
          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </form>
      )}
    </>
  );
};

export default withTranslation()(RepaymentTimeForm);
